import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ImageUploadField, PageHeader, useToast } from 'components/admin';
import { Button, Checkbox, Input, TreeSelect, Select } from 'components/common';
import { createProductSolutionGroup, getAdminProductSolutionGroupById, updateProductSolutionGroup } from 'apis/adminProductSolutionGroups';
import { getAdminEcosystemGroups } from 'apis/adminEcosystemGroups';
import { useActiveLanguagesQuery, useAdminCreateMutation, useAdminDetailQuery, useAdminListQuery, useAdminUpdateMutation } from 'hooks';
import axiosInstance from 'config/axiosInstance';
import { API_ENDPOINTS } from 'constants/apiEndpoints';
import { buildMenuTree, findMenuByKey, flattenMenuTree } from 'utils/menuTree';
import '../../../styles/admin-shared.scss';

const getProductMenuOptions = (items = []) => {
    const tree = buildMenuTree(items);
    const productsMenu = findMenuByKey(tree, 'menu.products');
    return productsMenu?.children || [];
};

const findMenuById = (items = [], id) => {
    for (const item of items) {
        if (Number(item.id) === Number(id)) return item;
        const nested = findMenuById(item.children || [], id);
        if (nested) return nested;
    }
    return null;
};

const ProductSolutionGroupsForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const [autoFilling, setAutoFilling] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [navMenus, setNavMenus] = useState([]);
    // true khi navMenuId đã được chọn → lock name & slug để tránh vênh với navMenu
    const [isNavMenuLinked, setIsNavMenuLinked] = useState(false);
    const [formData, setFormData] = useState({
        navMenuId: '',
        ecosystemGroupId: '',
        image: '',
        sortOrder: 0,
        isActive: true,
        translation: [],
    });

    const { data: languages = [] } = useActiveLanguagesQuery();
    const { data: groupsData = { items: [] } } = useAdminListQuery(
        'ecosystem-groups-options',
        { pageSize: 100, languageCode: 'vi' },
        getAdminEcosystemGroups
    );
    const ecosystemGroups = groupsData.items || [];
    const { data: detail, error: detailError } = useAdminDetailQuery('product-solution-groups', id, getAdminProductSolutionGroupById);
    const createMutation = useAdminCreateMutation('product-solution-groups', createProductSolutionGroup);
    const updateMutation = useAdminUpdateMutation('product-solution-groups', updateProductSolutionGroup);
    const loading = createMutation.isPending || updateMutation.isPending;

    useEffect(() => {
        if (!id && languages.length) setFormData(prev => ({ ...prev, translation: languages.map(l => ({ languageId: l.id, name: '', slug: '' })) }));
    }, [id, languages]);

    useEffect(() => {
        axiosInstance.get(API_ENDPOINTS.ADMIN.NAV_MENUS.GET_PUBLIC('vi'))
            .then(res => {
                setNavMenus(getProductMenuOptions(res.data?.data || []));
            }).catch(() => { });
    }, []);

    useEffect(() => {
        if (detail) {
            setFormData({ ...detail, navMenuId: detail.navMenuId || '', ecosystemGroupId: detail.ecosystemGroupId || '', translation: detail.translation || [] });
            if (detail.navMenuId) setIsNavMenuLinked(true);
        }
    }, [detail]);

    useEffect(() => { if (detailError) toast.error(detailError.message || 'Tải dữ liệu thất bại'); }, [detailError, toast]);

    const handleNavMenuChange = async (e) => {
        const selectedId = e.target.value;

        if (!selectedId) {
            setIsNavMenuLinked(false);
            setFormData(prev => ({
                ...prev,
                navMenuId: '',
                sortOrder: 0,
                translation: prev.translation.map(t => ({ ...t, name: '', slug: '' })),
            }));
            return;
        }

        const numericId = Number(selectedId);
        const flatNavMenus = flattenMenuTree(navMenus);
        const matchedIndex = flatNavMenus.findIndex(m => Number(m.id) === numericId);
        const autoSortOrder = matchedIndex !== -1 ? matchedIndex + 1 : 0;

        setAutoFilling(true);
        try {
            const results = await Promise.all(
                languages.map(lang =>
                    axiosInstance
                        .get(API_ENDPOINTS.ADMIN.NAV_MENUS.GET_PUBLIC(lang.code))
                        .then(res => {
                            const menuOptions = getProductMenuOptions(res.data?.data || []);
                            const menuItem = findMenuById(menuOptions, numericId);
                            return {
                                languageId: lang.id,
                                name: menuItem?.title || '',
                                slug: menuItem?.slug || '',
                            };
                        })
                        .catch(() => ({ languageId: lang.id, name: '', slug: '' }))
                )
            );

            setFormData(prev => ({
                ...prev,
                navMenuId: numericId,
                sortOrder: autoSortOrder,
                translation: prev.translation.map(t => {
                    const found = results.find(r => r.languageId === t.languageId);
                    return found ? { ...t, name: found.name, slug: found.slug } : t;
                }),
            }));
            setIsNavMenuLinked(true);
        } catch {
            toast.error('Khởi tạo tự động từ Menu thất bại');
        } finally {
            setAutoFilling(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleTranslationChange = (langId, field, value) => {
        setFormData(prev => ({ ...prev, translation: prev.translation.map(t => t.languageId === langId ? { ...t, [field]: value } : t) }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...formData,
                navMenuId: formData.navMenuId ? Number(formData.navMenuId) : null,
                ecosystemGroupId: formData.ecosystemGroupId || null,
                sortOrder: Number(formData.sortOrder),
            };
            const res = id
                ? await updateMutation.mutateAsync({ id, data: payload })
                : await createMutation.mutateAsync(payload);
            if (res.success) {
                toast.success(id ? 'Cập nhật thành công' : 'Thêm mới thành công');
                navigate('/admin/product-solution-groups');
            } else {
                toast.error(res.message || 'Lưu thất bại');
            }
        } catch {
            toast.error('Lưu thất bại');
        }
    };

    const ecosystemOptions = [
        { value: '', label: 'Không thuộc hệ sinh thái (Mặc định)' },
        ...ecosystemGroups.map(g => ({ value: g.id, label: g.name || g.code }))
    ];

    return (
        <div className="admin-page">
            <PageHeader title={id ? 'Cập nhật Nhóm Sản phẩm - Giải pháp' : 'Thêm Nhóm Sản phẩm - Giải pháp'} />
            <form className="form-card" onSubmit={handleSubmit}>
                <p className="form-section-title">Cài đặt nhóm</p>
                <div className="form-row">
                    <TreeSelect
                        label="Liên kết với Menu điều hướng (Tùy chọn)"
                        name="navMenuId"
                        value={formData.navMenuId}
                        onChange={handleNavMenuChange}
                        items={navMenus}
                        placeholder="-- Không chọn (Nhập tay độc lập) --"
                    />
                    <Select
                        label="Hệ sinh thái (Tùy chọn)"
                        name="ecosystemGroupId"
                        value={formData.ecosystemGroupId}
                        onChange={handleChange}
                        options={ecosystemOptions}
                    />
                    <Input
                        label="Thứ tự sắp xếp"
                        name="sortOrder"
                        type="number"
                        value={formData.sortOrder}
                        onChange={handleChange}
                        readOnly={isNavMenuLinked}
                        title={isNavMenuLinked ? 'Thứ tự được đồng bộ tự động từ Menu điều hướng' : ''}
                    />
                </div>
                <ImageUploadField
                    label="Hình ảnh nhóm giải pháp"
                    value={formData.image}
                    folder="product-solutions/groups"
                    onChange={(image) => setFormData(prev => ({ ...prev, image }))}
                />
                <Checkbox label="Hoạt động" name="isActive" checked={formData.isActive} onChange={handleChange} />

                {languages.length > 0 && (
                    <>
                        <p className="form-section-title">
                            Bản dịch
                            {isNavMenuLinked && (
                                <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#f59e0b', marginLeft: '10px' }}>
                                    🔒 Tên và Slug được đồng bộ từ Menu điều hướng — không thể chỉnh sửa tay
                                </span>
                            )}
                        </p>
                        <div className="translation-tabs">
                            <div className="tab-header">
                                {languages.map((lang, i) => (
                                    <button key={lang.id} type="button" className={`tab-btn ${activeTab === i ? 'active' : ''}`} onClick={() => setActiveTab(i)}>
                                        {lang.name}
                                    </button>
                                ))}
                            </div>
                            {autoFilling && (
                                <div style={{ padding: '16px', textAlign: 'center', color: '#6b7280', fontStyle: 'italic' }}>
                                    ⏳ Đang tự động điền dữ liệu từ Menu...
                                </div>
                            )}
                            {!autoFilling && languages.map((lang, i) => {
                                const t = formData.translation.find(t => t.languageId === lang.id) || {};
                                return activeTab === i ? (
                                    <div key={lang.id} className="tab-content">
                                        <Input
                                            label="Tên *"
                                            value={t.name || ''}
                                            onChange={e => handleTranslationChange(lang.id, 'name', e.target.value)}
                                            required
                                            readOnly={isNavMenuLinked}
                                            title={isNavMenuLinked ? 'Được đồng bộ tự động từ Menu điều hướng' : ''}
                                        />
                                        <Input
                                            label="Slug"
                                            value={t.slug || ''}
                                            onChange={e => handleTranslationChange(lang.id, 'slug', e.target.value)}
                                            readOnly={isNavMenuLinked}
                                            title={isNavMenuLinked ? 'Được đồng bộ tự động từ Menu điều hướng — thay đổi tại navMenu để cập nhật' : ''}
                                        />
                                    </div>
                                ) : null;
                            })}
                        </div>
                    </>
                )}
                <div className="form-actions">
                    <Button type="submit" disabled={loading || autoFilling}>{loading ? 'Đang lưu...' : id ? 'Cập nhật' : 'Thêm mới'}</Button>
                    <Button type="button" variant="secondary" onClick={() => navigate('/admin/product-solution-groups')}>Hủy</Button>
                </div>
            </form>
        </div>
    );
};

export default ProductSolutionGroupsForm;
